import axios from 'axios';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthnAutofill,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { API_URL } from '../../config/api';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export interface PasskeyRow {
  id: string;
  name: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

/** Whether this browser can do WebAuthn at all. */
export function passkeySupported(): boolean {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && typeof navigator.credentials?.create === 'function';
}

/** Register a new passkey for the logged-in user. Returns true on success. */
export async function registerPasskey(name?: string): Promise<void> {
  const optionsRes = await axios.post(`${API_URL}/api/passkey/register/options`, {}, { headers: auth() });
  const attResp = await startRegistration({ optionsJSON: optionsRes.data });
  await axios.post(
    `${API_URL}/api/passkey/register/verify`,
    { response: attResp, name },
    { headers: auth() },
  );
}

/** Whether this browser can show passkeys inline in the email field (autofill). */
export const passkeyAutofillSupported = () => browserSupportsWebAuthnAutofill();

/**
 * Cancel any in-flight WebAuthn request (i.e. the pending autofill one).
 * Only ONE WebAuthn call may be outstanding at a time, so the button-triggered
 * login must clear the armed autofill request first or the browser rejects it.
 */
export const cancelPendingPasskey = () => WebAuthnAbortService.cancelCeremony();

/**
 * Whether this address has a passkey we could prompt for, WITHOUT opening any
 * OS sheet. The server returns allowCredentials only when it found passkeys for
 * the address, so an empty/absent list means "nothing to prompt with".
 *
 * Not an enumeration oracle: an unknown email and a known email with no
 * passkeys both come back the same way (no allowCredentials) — the server
 * deliberately does not distinguish them, and neither does this.
 */
export async function passkeyExistsFor(email: string): Promise<boolean> {
  try {
    const res = await axios.post(`${API_URL}/api/passkey/login/options`, { email });
    return (res.data?.allowCredentials?.length ?? 0) > 0;
  } catch {
    return false;
  }
}

/**
 * Log in with a passkey. Returns the auth payload ({token, refreshToken, user}).
 *
 * `useAutofill` switches on conditional mediation: instead of opening a modal,
 * the browser offers the passkey inline from the email field (autocomplete
 *="username webauthn") and resolves only once the user picks one. That call
 * stays pending indefinitely, so callers must treat an abort as normal.
 */
export async function loginWithPasskey(
  email?: string,
  useAutofill = false,
): Promise<{ token: string; refreshToken: string; user: any }> {
  const optionsRes = await axios.post(`${API_URL}/api/passkey/login/options`, { email });
  const options = optionsRes.data;
  const authResp = await startAuthentication({ optionsJSON: options, useBrowserAutofill: useAutofill });
  const verifyRes = await axios.post(`${API_URL}/api/passkey/login/verify`, {
    response: authResp,
    challenge: options.challenge,
  });
  return verifyRes.data;
}

export async function listPasskeys(): Promise<PasskeyRow[]> {
  const res = await axios.get(`${API_URL}/api/passkey`, { headers: auth() });
  return res.data.passkeys;
}

export async function deletePasskey(id: string): Promise<void> {
  await axios.delete(`${API_URL}/api/passkey/${id}`, { headers: auth() });
}
